import axiosInstance from "@/app/lib/api-client";
import type {
  AsyncApiResponse,
  ExtendedAPIParams,
  OrgMembership,
  User,
} from "@/app/lib/definitions";
import type {
  ExtractedRole,
  FileImportConfirmResponse,
  HierarchyExtractionResult,
  PendingUser,
} from "@/app/lib/definitions/user";
import { PaginatedResponse } from "@/app/lib/hooks/usePaginatedQuery";

export const getUsers = (params: ExtendedAPIParams = {}) =>
  axiosInstance
    .get<PaginatedResponse<User>>("/users/", { params })
    .then((response) => response.data);

export const getUser = async (id: string, params: ExtendedAPIParams = {}) => {
  return axiosInstance.get<User>(`/users/${id}/`, { params }).then((response) => response.data);
};

export const getMyUser = async () => {
  const url = "/users/me/";
  return axiosInstance.get<User>(url).then((response) => response.data);
};

export const getMyOrgMemberships = async () => {
  const url = "/users/my-org-memberships/";
  return axiosInstance.get<OrgMembership[]>(url).then((response) => response.data);
};

export const createUser = (formData: FormData) =>
  axiosInstance.post<User>("/users/", formData).then((response) => response.data);

export const updateUser = (id: string, formData: Partial<User> | FormData) =>
  axiosInstance
    .patch<User>(`/users/${id}/`, formData, {
      headers: formData instanceof FormData ? {} : { "Content-Type": "application/json" },
    })
    .then((response) => response.data);

export const inviteUsers = (ids: string[]) =>
  axiosInstance
    .post<{ message: string }>("/users/invite/", { ids })
    .then((response) => response.data);

export const deactivateUser = async (user: User) => {
  await axiosInstance.delete<{ message: string }>(`/users/${user.id}/`);
};

export const getUserLimits = async () => {
  return axiosInstance
    .get<{
      current_users: number;
      max_users: number;
      can_create_new_users: boolean;
    }>("/users/limits/")
    .then((response) => response.data);
};

export const getSuggestedPrompt = async (node_name: string) => {
  return axiosInstance
    .post<{ prompt: string }>("/users/get-suggested-prompt/", { node_name })
    .then((response) => response.data);
};

export const bulkCreateUsers = (data: { users: any[]; invite: boolean }) =>
  axiosInstance
    .post<{
      created_count: number;
      error_count: number;
      errors?: any[];
    }>("/users/bulk-create/", data)
    .then((response) => response.data);

export const createAiAdvisors = (data: { roles: string[]; company_context: string }) =>
  axiosInstance
    .post<{
      task_id: string;
      detail: string;
      roles: string[];
    }>("/users/create-ai-advisors/", data)
    .then((response) => response.data);

// File Import
export const uploadForFileImport = (formData: FormData) =>
  axiosInstance
    .post<AsyncApiResponse<HierarchyExtractionResult>>("/users/import-from-file/", formData)
    .then((response) => response.data);

export const confirmFileImport = (data: {
  roles: ExtractedRole[];
  artifact_ref_id?: string | null;
  invite: boolean;
}) =>
  axiosInstance
    .post<FileImportConfirmResponse>("/users/confirm-file-import/", data)
    .then((response) => response.data);

// Pending Users
export const getPendingUsers = (params: ExtendedAPIParams = {}) =>
  axiosInstance
    .get<PaginatedResponse<PendingUser>>("/pending-users/", { params })
    .then((response) => response.data);

export const getPendingUser = (id: number, params: ExtendedAPIParams = {}) =>
  axiosInstance
    .get<PendingUser>(`/pending-users/${id}/`, { params })
    .then((response) => response.data);

export const convertPendingUser = (
  pendingUserId: number,
  sendInvite: boolean = false,
  membershipType: string = "employee",
  email?: string,
  name?: string,
  params: ExtendedAPIParams = {},
) =>
  axiosInstance
    .post<User>(
      `/pending-users/${pendingUserId}/convert/`,
      {
        send_invite: sendInvite,
        membership_type: membershipType,
        ...(email && { email }),
        ...(name && { name }),
      },
      { params },
    )
    .then((response) => response.data);

export const dismissPendingUser = (pendingUserId: number, params: ExtendedAPIParams = {}) =>
  axiosInstance
    .post<{ detail: string }>(`/pending-users/${pendingUserId}/dismiss/`, {}, { params })
    .then((response) => response.data);
